/*
 * PKI.Oid — object identifier registry.
 *
 * Turns 1.2.840.113549.1.1.11 into "SHA-256 with RSA". Cheap to maintain and
 * high leverage: every tool that shows a parsed structure reads its labels from
 * here, so an unknown OID is a one-line fix in one file rather than a mystery
 * number on six pages.
 *
 * Unknown OIDs are never hidden — `label()` falls back to the dotted form so a
 * niche CA extension shows up as itself instead of vanishing.
 */
(function (root) {
    'use strict';

    /* ─── signature algorithms ──────────────────────────────────────── */
    // hash/key split is kept as data: the analyzer grades on `hash` (SHA-1 is
    // fatal) and `key` (RSA vs ECDSA), so it must not re-parse the label text.
    var SIG = {
        '1.2.840.113549.1.1.2':  { label: 'MD2 with RSA',        key: 'RSA', hash: 'MD2' },
        '1.2.840.113549.1.1.3':  { label: 'MD4 with RSA',        key: 'RSA', hash: 'MD4' },
        '1.2.840.113549.1.1.4':  { label: 'MD5 with RSA',        key: 'RSA', hash: 'MD5' },
        '1.2.840.113549.1.1.5':  { label: 'SHA-1 with RSA',      key: 'RSA', hash: 'SHA-1' },
        '1.2.840.113549.1.1.10': { label: 'RSASSA-PSS',          key: 'RSA', hash: null },
        '1.2.840.113549.1.1.11': { label: 'SHA-256 with RSA',    key: 'RSA', hash: 'SHA-256' },
        '1.2.840.113549.1.1.12': { label: 'SHA-384 with RSA',    key: 'RSA', hash: 'SHA-384' },
        '1.2.840.113549.1.1.13': { label: 'SHA-512 with RSA',    key: 'RSA', hash: 'SHA-512' },
        '1.2.840.113549.1.1.14': { label: 'SHA-224 with RSA',    key: 'RSA', hash: 'SHA-224' },
        '1.2.840.10040.4.3':     { label: 'SHA-1 with DSA',      key: 'DSA', hash: 'SHA-1' },
        '2.16.840.1.101.3.4.3.2':{ label: 'SHA-256 with DSA',    key: 'DSA', hash: 'SHA-256' },
        '1.2.840.10045.4.1':     { label: 'SHA-1 with ECDSA',    key: 'EC',  hash: 'SHA-1' },
        '1.2.840.10045.4.3.1':   { label: 'SHA-224 with ECDSA',  key: 'EC',  hash: 'SHA-224' },
        '1.2.840.10045.4.3.2':   { label: 'SHA-256 with ECDSA',  key: 'EC',  hash: 'SHA-256' },
        '1.2.840.10045.4.3.3':   { label: 'SHA-384 with ECDSA',  key: 'EC',  hash: 'SHA-384' },
        '1.2.840.10045.4.3.4':   { label: 'SHA-512 with ECDSA',  key: 'EC',  hash: 'SHA-512' },
        '1.3.101.112':           { label: 'Ed25519',             key: 'Ed25519', hash: 'SHA-512' },
        '1.3.101.113':           { label: 'Ed448',               key: 'Ed448', hash: 'SHAKE256' },
        // GOST shows up on Russian government sites — label it rather than
        // print a raw OID at a Russian-language audience.
        '1.2.643.7.1.1.3.2':     { label: 'ГОСТ Р 34.10-2012 (256)', key: 'GOST', hash: 'GOST R 34.11-2012' },
        '1.2.643.7.1.1.3.3':     { label: 'ГОСТ Р 34.10-2012 (512)', key: 'GOST', hash: 'GOST R 34.11-2012' },
        '1.2.643.2.2.3':         { label: 'ГОСТ Р 34.10-2001',   key: 'GOST', hash: 'GOST R 34.11-94' }
    };

    /* ─── public key algorithms ─────────────────────────────────────── */
    var PUBKEY = {
        '1.2.840.113549.1.1.1': { label: 'RSA', kind: 'RSA' },
        '1.2.840.113549.1.1.10':{ label: 'RSASSA-PSS', kind: 'RSA' },
        '1.2.840.10045.2.1':    { label: 'EC', kind: 'EC' },
        '1.2.840.10040.4.1':    { label: 'DSA', kind: 'DSA' },
        '1.2.840.113549.1.3.1': { label: 'Diffie-Hellman', kind: 'DH' },
        '1.3.101.110':          { label: 'X25519', kind: 'X25519' },
        '1.3.101.111':          { label: 'X448', kind: 'X448' },
        '1.3.101.112':          { label: 'Ed25519', kind: 'Ed25519' },
        '1.3.101.113':          { label: 'Ed448', kind: 'Ed448' },
        // ГОСТ — ключи квалифицированных подписей РФ. Проверяются pki/gost.js,
        // WebCrypto их не знает.
        '1.2.643.7.1.1.1.1':    { label: 'ГОСТ Р 34.10-2012 (256)', kind: 'GOST' },
        '1.2.643.7.1.1.1.2':    { label: 'ГОСТ Р 34.10-2012 (512)', kind: 'GOST' },
        '1.2.643.2.2.19':       { label: 'ГОСТ Р 34.10-2001', kind: 'GOST' }
    };

    /* ─── hashes ────────────────────────────────────────────────────── */
    var HASH = {
        '1.2.840.113549.2.2': 'MD2',
        '1.2.840.113549.2.5': 'MD5',
        '1.3.14.3.2.26': 'SHA-1',
        '2.16.840.1.101.3.4.2.1': 'SHA-256',
        '2.16.840.1.101.3.4.2.2': 'SHA-384',
        '2.16.840.1.101.3.4.2.3': 'SHA-512',
        '2.16.840.1.101.3.4.2.4': 'SHA-224',
        '2.16.840.1.101.3.4.2.7': 'SHA3-224',
        '2.16.840.1.101.3.4.2.8': 'SHA3-256',
        '2.16.840.1.101.3.4.2.9': 'SHA3-384',
        '2.16.840.1.101.3.4.2.10': 'SHA3-512',
        '1.2.643.7.1.1.2.2': 'GOST R 34.11-2012 (256)',
        '1.2.643.7.1.1.2.3': 'GOST R 34.11-2012 (512)',
        '1.2.643.2.2.9': 'GOST R 34.11-94'
    };

    /* ─── named curves ──────────────────────────────────────────────── */
    // `bits` feeds the strength check; `webcrypto` is the name to hand to
    // crypto.subtle (null = WebCrypto cannot do it, tool must say so).
    var CURVE = {
        '1.2.840.10045.3.1.7': { label: 'P-256 (prime256v1 / secp256r1)', bits: 256, webcrypto: 'P-256' },
        '1.3.132.0.34':        { label: 'P-384 (secp384r1)',              bits: 384, webcrypto: 'P-384' },
        '1.3.132.0.35':        { label: 'P-521 (secp521r1)',              bits: 521, webcrypto: 'P-521' },
        '1.2.840.10045.3.1.1': { label: 'P-192 (prime192v1)',             bits: 192, webcrypto: null },
        '1.3.132.0.33':        { label: 'P-224 (secp224r1)',              bits: 224, webcrypto: null },
        '1.3.132.0.10':        { label: 'secp256k1',                      bits: 256, webcrypto: null },
        '1.3.36.3.3.2.8.1.1.7':{ label: 'brainpoolP256r1',                bits: 256, webcrypto: null },
        '1.3.36.3.3.2.8.1.1.11':{label: 'brainpoolP384r1',                bits: 384, webcrypto: null },
        '1.3.36.3.3.2.8.1.1.13':{label: 'brainpoolP512r1',                bits: 512, webcrypto: null }
    };

    /* ─── distinguished-name attribute types ────────────────────────── */
    // `short` is the RFC 4514 abbreviation used when rendering a DN string.
    // `hint` explains the field to a user filling in a CSR — most people have
    // never been told that OU is dead or that C must be two letters.
    var RDN = {
        '2.5.4.3':  { short: 'CN', label: 'Common Name' },
        '2.5.4.6':  { short: 'C',  label: 'Country' },
        '2.5.4.7':  { short: 'L',  label: 'Locality' },
        '2.5.4.8':  { short: 'ST', label: 'State / Province' },
        '2.5.4.9':  { short: 'STREET', label: 'Street Address' },
        '2.5.4.10': { short: 'O',  label: 'Organization' },
        '2.5.4.11': { short: 'OU', label: 'Organizational Unit' },
        '2.5.4.4':  { short: 'SN', label: 'Surname' },
        '2.5.4.42': { short: 'GN', label: 'Given Name' },
        '2.5.4.5':  { short: 'serialNumber', label: 'Serial Number' },
        '2.5.4.12': { short: 'title', label: 'Title' },
        '2.5.4.13': { short: 'description', label: 'Description' },
        '2.5.4.15': { short: 'businessCategory', label: 'Business Category' },
        '2.5.4.17': { short: 'postalCode', label: 'Postal Code' },
        '2.5.4.20': { short: 'telephoneNumber', label: 'Telephone' },
        '2.5.4.65': { short: 'pseudonym', label: 'Pseudonym' },
        '2.5.4.97': { short: 'organizationIdentifier', label: 'Organization Identifier' },
        '1.2.840.113549.1.9.1': { short: 'emailAddress', label: 'Email Address' },
        '0.9.2342.19200300.100.1.25': { short: 'DC', label: 'Domain Component' },
        '0.9.2342.19200300.100.1.1':  { short: 'UID', label: 'User ID' },
        // EV jurisdiction fields — seeing these is how you know it is an EV cert.
        '1.3.6.1.4.1.311.60.2.1.1': { short: 'jurisdictionL', label: 'Jurisdiction Locality' },
        '1.3.6.1.4.1.311.60.2.1.2': { short: 'jurisdictionST', label: 'Jurisdiction State' },
        '1.3.6.1.4.1.311.60.2.1.3': { short: 'jurisdictionC', label: 'Jurisdiction Country' },
        // Российские квалифицированные сертификаты (Приказ ФСБ № 795).
        '1.2.643.3.131.1.1': { short: 'INN', label: 'INN (RU taxpayer number)' },
        '1.2.643.100.4':     { short: 'INNLE', label: 'INN of legal entity (RU)' },
        '1.2.643.100.1':     { short: 'OGRN', label: 'OGRN (RU registration number)' },
        '1.2.643.100.5':     { short: 'OGRNIP', label: 'OGRNIP (RU sole trader number)' },
        '1.2.643.100.3':     { short: 'SNILS', label: 'SNILS (RU insurance number)' }
    };

    /* ─── certificate extensions ────────────────────────────────────── */
    var EXT = {
        '2.5.29.14': 'Subject Key Identifier',
        '2.5.29.15': 'Key Usage',
        '2.5.29.17': 'Subject Alternative Name',
        '2.5.29.18': 'Issuer Alternative Name',
        '2.5.29.19': 'Basic Constraints',
        '2.5.29.20': 'CRL Number',
        '2.5.29.21': 'CRL Reason',
        '2.5.29.30': 'Name Constraints',
        '2.5.29.31': 'CRL Distribution Points',
        '2.5.29.32': 'Certificate Policies',
        '2.5.29.33': 'Policy Mappings',
        '2.5.29.35': 'Authority Key Identifier',
        '2.5.29.36': 'Policy Constraints',
        '2.5.29.37': 'Extended Key Usage',
        '2.5.29.54': 'Inhibit anyPolicy',
        '2.5.29.9':  'Subject Directory Attributes',
        '1.3.6.1.5.5.7.1.1':  'Authority Information Access',
        '1.3.6.1.5.5.7.1.11': 'Subject Information Access',
        '1.3.6.1.5.5.7.1.24': 'TLS Feature (OCSP Must-Staple)',
        '1.3.6.1.4.1.11129.2.4.2': 'Signed Certificate Timestamps (CT)',
        '1.3.6.1.4.1.11129.2.4.3': 'CT Precertificate Poison',
        '1.3.6.1.5.5.7.48.1.5': 'OCSP No-Check',
        '1.3.6.1.4.1.311.21.7': 'Microsoft Certificate Template',
        '1.3.6.1.4.1.311.21.10': 'Microsoft Application Policies',
        '1.3.6.1.4.1.311.20.2': 'Microsoft Certificate Type',
        '2.16.840.1.113730.1.1': 'Netscape Certificate Type',
        '2.16.840.1.113730.1.13': 'Netscape Comment',
        '1.2.643.100.111': 'Subject Sign Tool (RU)',
        '1.2.643.100.112': 'Issuer Sign Tool (RU)',
        '1.2.643.100.114': 'Identification Kind (RU)'
    };

    /* ─── extended key usage ────────────────────────────────────────── */
    var EKU = {
        '1.3.6.1.5.5.7.3.1': 'TLS Web Server Authentication',
        '1.3.6.1.5.5.7.3.2': 'TLS Web Client Authentication',
        '1.3.6.1.5.5.7.3.3': 'Code Signing',
        '1.3.6.1.5.5.7.3.4': 'Email Protection (S/MIME)',
        '1.3.6.1.5.5.7.3.5': 'IPSec End System',
        '1.3.6.1.5.5.7.3.6': 'IPSec Tunnel',
        '1.3.6.1.5.5.7.3.7': 'IPSec User',
        '1.3.6.1.5.5.7.3.8': 'Time Stamping',
        '1.3.6.1.5.5.7.3.9': 'OCSP Signing',
        '1.3.6.1.5.5.7.3.36': 'Document Signing',
        '1.3.6.1.4.1.311.10.3.12': 'Microsoft Document Signing',
        '1.3.6.1.5.5.7.3.14': 'EAP over PPP',
        '1.3.6.1.5.5.7.3.15': 'EAP over LAN',
        '1.3.6.1.4.1.311.10.3.3': 'Microsoft Server Gated Crypto',
        '1.3.6.1.4.1.311.10.3.4': 'Microsoft Encrypting File System',
        '1.3.6.1.4.1.311.20.2.2': 'Microsoft Smart Card Logon',
        '2.16.840.1.113730.4.1': 'Netscape Server Gated Crypto',
        '2.5.29.37.0': 'Any Extended Key Usage'
    };

    /* ─── access methods (AIA / SIA) ────────────────────────────────── */
    var ACCESS = {
        '1.3.6.1.5.5.7.48.1': 'OCSP',
        '1.3.6.1.5.5.7.48.2': 'CA Issuers',
        '1.3.6.1.5.5.7.48.3': 'Time Stamping',
        '1.3.6.1.5.5.7.48.5': 'CA Repository'
    };

    /* ─── certificate policies ──────────────────────────────────────── */
    // Validation level is what a user actually wants to know ("is this DV?"),
    // and it is only ever visible through these OIDs.
    var POLICY = {
        '2.23.140.1.2.1': { label: 'CA/B Forum Domain Validated (DV)', level: 'DV' },
        '2.23.140.1.2.2': { label: 'CA/B Forum Organization Validated (OV)', level: 'OV' },
        '2.23.140.1.2.3': { label: 'CA/B Forum Individual Validated (IV)', level: 'IV' },
        '2.23.140.1.1':   { label: 'CA/B Forum Extended Validation (EV)', level: 'EV' },
        '2.23.140.1.4.1': { label: 'CA/B Forum Code Signing', level: null },
        '2.23.140.1.5.1.1': { label: 'CA/B Forum S/MIME Mailbox Validated (Legacy)', level: null },
        '2.23.140.1.5.2.1': { label: 'CA/B Forum S/MIME Organization Validated (Legacy)', level: null },
        '2.5.29.32.0':    { label: 'Any Policy', level: null }
    };

    /* ─── PKCS / algorithm identifiers used by the container formats ── */
    var PKCS = {
        // PKCS#7 / CMS content types
        '1.2.840.113549.1.7.1': 'data',
        '1.2.840.113549.1.7.2': 'signedData',
        '1.2.840.113549.1.7.6': 'encryptedData',
        // PKCS#12 bag types
        '1.2.840.113549.1.12.10.1.1': 'keyBag',
        '1.2.840.113549.1.12.10.1.2': 'pkcs8ShroudedKeyBag',
        '1.2.840.113549.1.12.10.1.3': 'certBag',
        '1.2.840.113549.1.12.10.1.4': 'crlBag',
        '1.2.840.113549.1.12.10.1.5': 'secretBag',
        '1.2.840.113549.1.12.10.1.6': 'safeContentsBag',
        '1.2.840.113549.1.9.22.1': 'x509Certificate',
        // PKCS#9 attributes
        '1.2.840.113549.1.9.20': 'friendlyName',
        '1.2.840.113549.1.9.21': 'localKeyId',
        '1.2.840.113549.1.9.14': 'extensionRequest',
        '1.2.840.113549.1.9.7':  'challengePassword',
        // PKCS#5
        '1.2.840.113549.1.5.13': 'PBES2',
        '1.2.840.113549.1.5.12': 'PBKDF2',
        '1.2.840.113549.2.7':    'hmacWithSHA1',
        '1.2.840.113549.2.8':    'hmacWithSHA224',
        '1.2.840.113549.2.9':    'hmacWithSHA256',
        '1.2.840.113549.2.10':   'hmacWithSHA384',
        '1.2.840.113549.2.11':   'hmacWithSHA512',
        // PKCS#12 PBE (legacy but everywhere — Windows and old keytool)
        '1.2.840.113549.1.12.1.1': 'pbeWithSHAAnd128BitRC4',
        '1.2.840.113549.1.12.1.2': 'pbeWithSHAAnd40BitRC4',
        '1.2.840.113549.1.12.1.3': 'pbeWithSHAAnd3-KeyTripleDES-CBC',
        '1.2.840.113549.1.12.1.4': 'pbeWithSHAAnd2-KeyTripleDES-CBC',
        '1.2.840.113549.1.12.1.5': 'pbeWithSHAAnd128BitRC2-CBC',
        '1.2.840.113549.1.12.1.6': 'pbeWithSHAAnd40BitRC2-CBC',
        // Ciphers
        '2.16.840.1.101.3.4.1.2':  'aes128-CBC',
        '2.16.840.1.101.3.4.1.22': 'aes192-CBC',
        '2.16.840.1.101.3.4.1.42': 'aes256-CBC',
        '2.16.840.1.101.3.4.1.6':  'aes128-GCM',
        '2.16.840.1.101.3.4.1.46': 'aes256-GCM',
        '1.2.840.113549.3.7':      'des-EDE3-CBC',
        '1.2.840.113549.3.2':      'rc2-CBC',
        '1.3.14.3.2.7':            'des-CBC'
    };

    // One flat index so `label()` can answer for any OID without the caller
    // knowing which table it lives in.
    var ALL = {};
    function index(table, pick) {
        Object.keys(table).forEach(function (k) {
            if (ALL[k] === undefined) ALL[k] = pick ? pick(table[k]) : table[k];
        });
    }
    index(SIG, function (v) { return v.label; });
    index(PUBKEY, function (v) { return v.label; });
    index(HASH);
    index(CURVE, function (v) { return v.label; });
    index(EXT);
    index(EKU);
    index(ACCESS);
    index(POLICY, function (v) { return v.label; });
    index(PKCS);
    index(RDN, function (v) { return v.label; });

    /** Human label for any OID; falls back to the dotted form. */
    function label(oid) {
        if (!oid) return '';
        return ALL[oid] || oid;
    }

    /** RFC 4514 short name for a DN attribute (CN, O, …), else the OID. */
    function rdnShort(oid) {
        return (RDN[oid] && RDN[oid].short) || oid;
    }

    function sigInfo(oid) {
        return SIG[oid] || { label: label(oid), key: null, hash: null };
    }

    function curveByWebCryptoName(name) {
        var found = null;
        Object.keys(CURVE).forEach(function (k) {
            if (CURVE[k].webcrypto === name && !found) found = { oid: k, info: CURVE[k] };
        });
        return found;
    }

    /** Reverse lookup: signature OID for (keyKind, hashName). */
    function sigOidFor(keyKind, hashName) {
        var found = null;
        Object.keys(SIG).forEach(function (k) {
            var s = SIG[k];
            if (!found && s.key === keyKind && s.hash === hashName) found = k;
        });
        return found;
    }

    var API = {
        SIG: SIG, PUBKEY: PUBKEY, HASH: HASH, CURVE: CURVE, RDN: RDN,
        EXT: EXT, EKU: EKU, ACCESS: ACCESS, POLICY: POLICY, PKCS: PKCS,
        label: label, rdnShort: rdnShort, sigInfo: sigInfo,
        curveByWebCryptoName: curveByWebCryptoName, sigOidFor: sigOidFor
    };

    if (typeof module !== 'undefined' && module.exports) module.exports = API;
    if (root) { root.PKI = root.PKI || {}; root.PKI.Oid = API; }
})(typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : null));
